import { useState, useEffect, lazy, Suspense, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Building2, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetTrigger, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { StatusBadge } from '@/components/custom/StatusBadge';
import { PriorityBadge } from '@/components/custom/PriorityBadge';
import { TypeBadge } from '@/components/custom/TypeBadge';
import { useAuth } from '@/hooks/useAuth';
import { fetchTicketEnrichment, fetchOrderWorkflow, fetchAttachedJvidEnrichment } from '@/hooks/useTickets';
import type {
  TicketDetail, TicketEnrichmentResponse, OrderWorkflowEntry,
  EnrichmentSubscription, DmpOrderResponse,
} from '@uniticket/shared';

// Leaflet (~150 KB) + its CSS only load when the drawer opens.
const EnrichmentMap = lazy(() => import('./EnrichmentMap'));

interface AddressEnrichmentPanelProps {
  ticket: TicketDetail;
  /**
   * Which JVID to enrich. Omitted = the ticket's own (the original behaviour). Supplied for a
   * JVID attached to the ticket, which is enriched through a different endpoint — the rendering,
   * role curation and order drill-down are identical either way.
   */
  jvid?: number;
  /** Replaces the default header, so an attached JVID can label itself. */
  title?: string;
  /** Replaces the default trigger-button label. */
  triggerLabel?: string;
}

const DASH = '—';

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? DASH : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** A simple label/value row used by the address + network blocks. */
function Row({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-slate-50 last:border-0">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">{label}</span>
      <span className="text-sm text-slate-700 text-right break-all">{value != null && value !== '' ? value : DASH}</span>
    </div>
  );
}

/**
 * Slide-out drawer with rich, multi-source JVID enrichment (mirrors the internal HC Address Search
 * app — see docs/JVID-Enrichment-Spec.md). All data arrives pre-curated per the user's operator
 * from the server, so the panel simply renders whichever blocks are present; internal-only blocks
 * (network, SSV/TSA, the Cocon map layer) are absent for external operators.
 */
export function AddressEnrichmentPanel({ ticket, jvid, title, triggerLabel }: AddressEnrichmentPanelProps) {
  const { t } = useTranslation(['tickets', 'common']);
  const { isInternal } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState<TicketEnrichmentResponse | null>(null);

  // Per-order workflow timeline, lazily fetched + cached on row expand.
  const [workflows, setWorkflows] = useState<Record<number, OrderWorkflowEntry[] | 'loading' | 'error'>>({});
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    const load = jvid == null
      ? fetchTicketEnrichment(ticket.id)
      : fetchAttachedJvidEnrichment(ticket.id, jvid);
    load
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, ticket.id, jvid]);

  function toggleOrder(orderId: number) {
    setExpandedOrder((cur) => (cur === orderId ? null : orderId));
    if (workflows[orderId] === undefined) {
      setWorkflows((w) => ({ ...w, [orderId]: 'loading' }));
      fetchOrderWorkflow(ticket.id, orderId)
        .then((res) => setWorkflows((w) => ({ ...w, [orderId]: res.data })))
        .catch(() => setWorkflows((w) => ({ ...w, [orderId]: 'error' })));
    }
  }

  const addr = data?.addressStatus ?? null;
  const netRows = data?.network ?? [];
  const subs = data?.subscriptions ?? [];
  const ssvTsa = data?.ssvTsa ?? [];
  const orders = data?.orders ?? [];
  const tickets = data?.tickets ?? [];
  const nearby = data?.nearby ?? [];
  const hasAny = !!addr || netRows.length || subs.length || ssvTsa.length || orders.length || tickets.length || nearby.length;
  // Ethernet vs Splitter heading hint from the subscription type.
  const isEthernet = subs[0]?.type === 'B2B_UTAC';

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <MapPin className="w-4 h-4" />
          {triggerLabel ?? t('tickets:enrichment.open')}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="max-w-xl w-full overflow-y-auto p-0">
        <div className="gradient-band px-6 py-4">
          <SheetTitle className="text-sm font-bold text-white uppercase tracking-wide">{title ?? t('tickets:enrichment.title')}</SheetTitle>
          <SheetDescription className="sr-only">{title ?? t('tickets:enrichment.title')}</SheetDescription>
        </div>

        <div className="p-6 space-y-5">
          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-72 w-full" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-6 w-2/3" />
            </div>
          )}

          {!loading && error && <p className="text-sm text-red-500">{t('tickets:enrichment.loadError')}</p>}

          {!loading && !error && data && !data.available && (
            <p className="text-sm text-slate-400">{t('tickets:enrichment.noData')}</p>
          )}

          {!loading && !error && data && data.available && (
            <>
              {/* Map — always at top */}
              <section className="space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">{t('tickets:enrichment.map')}</h4>
                {data.center && data.center.lat != null && data.center.lon != null ? (
                  <Suspense fallback={<Skeleton className="h-72 w-full" />}>
                    <EnrichmentMap
                      ticketId={ticket.id}
                      lat={data.center.lat}
                      lon={data.center.lon}
                      label={[data.center.streetName, data.center.houseNumber].filter(Boolean).join(' ') || String(data.center.jvid)}
                      // The server decides: coconAvailable already folds in the role AND the
                      // operator's network-visibility capability. Re-testing isInternal here
                      // would silently override a deliberate grant to an external operator.
                      showCoconToggle={data.coconAvailable}
                    />
                  </Suspense>
                ) : (
                  <p className="text-sm text-slate-400">{t('tickets:enrichment.noData')}</p>
                )}
              </section>

              {!hasAny && (
                <p className="text-sm text-slate-400">
                  {data.dmpError ? t('tickets:enrichment.dmpUnavailable') : t('tickets:enrichment.noData')}
                </p>
              )}

              <Accordion type="multiple" defaultValue={['address', 'orders']} className="space-y-2">
                {/* Address / Delivery / Status / POP */}
                {addr && (
                  <AccordionItem value="address" className="border border-slate-200 rounded-lg px-3">
                    <AccordionTrigger className="text-sm font-semibold text-slate-700 py-3">{t('tickets:enrichment.addressStatus')}</AccordionTrigger>
                    <AccordionContent>
                      <div className="pb-3">
                        <Row label={t('common:fields.jvid')} value={addr.jvid} />
                        <Row label={t('tickets:enrichment.fields.street')} value={[addr.streetName, addr.houseNumberExtension, addr.busNumber].filter(Boolean).join(' ')} />
                        <Row label={t('tickets:enrichment.fields.city')} value={[addr.municipality, addr.city].filter(Boolean).join(', ')} />
                        <Row label={t('tickets:enrichment.fields.delivery')} value={addr.deliveryStatus} />
                        <Row label={t('tickets:enrichment.fields.status')} value={addr.status} />
                        {addr.areaName != null && <Row label={t('tickets:enrichment.fields.area')} value={[addr.areaName, addr.areaId].filter(Boolean).join(' ')} />}
                        <Row label={t('tickets:enrichment.fields.centralPop')} value={addr.centralPop} />
                        <Row label={t('tickets:enrichment.fields.areaPop')} value={addr.areaPop} />
                        {addr.quadrant != null && <Row label={t('tickets:enrichment.fields.quadrant')} value={addr.quadrant} />}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Network: splitter / ODF / UTAC (internal-only). A JVID can have >1 row (e.g. two fibers). */}
                {netRows.length > 0 && (
                  <AccordionItem value="network" className="border border-slate-200 rounded-lg px-3">
                    <AccordionTrigger className="text-sm font-semibold text-slate-700 py-3">
                      {isEthernet ? t('tickets:enrichment.ethernet') : t('tickets:enrichment.networkDetails')}
                      {netRows.length > 1 ? ` (${netRows.length})` : ''}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pb-3 space-y-4">
                        {netRows.map((net, i) => (
                          <div key={i} className={netRows.length > 1 ? 'border-t border-slate-100 pt-3 first:border-t-0 first:pt-0' : undefined}>
                            {netRows.length > 1 && (
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                                {t('tickets:enrichment.fields.fiberNumber')} {net.addressFiberNumber ?? i + 1}
                              </p>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                              <Row label={t('tickets:enrichment.fields.areaPop')} value={net.areaPop} />
                              <Row label={t('tickets:enrichment.fields.operator')} value={net.operator} />
                              <Row label={t('tickets:enrichment.fields.reference')} value={net.customerReference} />
                              <Row label={t('tickets:enrichment.fields.rack')} value={net.splitterRackLabel} />
                              <Row label={t('tickets:enrichment.fields.label')} value={net.splitterLabel} />
                              <Row label={t('tickets:enrichment.fields.port')} value={net.splitterPortPosition} />
                              <Row label={t('tickets:enrichment.fields.portStatus')} value={net.splitterPortStatus} />
                              <Row label={t('tickets:enrichment.fields.patch')} value={net.splitterPatchStatus} />
                              <Row label={t('tickets:enrichment.fields.odf')} value={net.patchPanelRackLabel} />
                              <Row label={t('tickets:enrichment.fields.fiberStatus')} value={net.addressFiberStatus} />
                              <Row label={t('tickets:enrichment.fields.fiberNumber')} value={net.addressFiberNumber} />
                              <Row label={t('tickets:enrichment.fields.utac')} value={net.utac} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Orders + inline workflow timeline */}
                {orders.length > 0 && (
                  <AccordionItem value="orders" className="border border-slate-200 rounded-lg px-3">
                    <AccordionTrigger className="text-sm font-semibold text-slate-700 py-3">{t('tickets:enrichment.orderHistory')}</AccordionTrigger>
                    <AccordionContent>
                      <OrdersTable
                        orders={orders}
                        showSplitter={isInternal}
                        expandedOrder={expandedOrder}
                        workflows={workflows}
                        onToggle={toggleOrder}
                      />
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Subscriptions */}
                {subs.length > 0 && (
                  <AccordionItem value="subscriptions" className="border border-slate-200 rounded-lg px-3">
                    <AccordionTrigger className="text-sm font-semibold text-slate-700 py-3">{t('tickets:enrichment.subscriptions')}</AccordionTrigger>
                    <AccordionContent>
                      <SubscriptionsTable subs={subs} />
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* SSV / TSA status (internal-only) */}
                {ssvTsa.length > 0 && (
                  <AccordionItem value="ssvtsa" className="border border-slate-200 rounded-lg px-3">
                    <AccordionTrigger className="text-sm font-semibold text-slate-700 py-3">{t('tickets:enrichment.ssvTsa')}</AccordionTrigger>
                    <AccordionContent>
                      <div className="pb-3 space-y-2">
                        {ssvTsa.map((s, i) => (
                          <div key={i} className="text-sm">
                            {s.task && <p className="font-mono text-xs text-slate-500 mb-1">{s.task}</p>}
                            <Row label="SSV" value={s.ssvStatus} />
                            <Row label="TSA" value={s.tsaStatus} />
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Tickets at this JVID */}
                {tickets.length > 0 && (
                  <AccordionItem value="tickets" className="border border-slate-200 rounded-lg px-3">
                    <AccordionTrigger className="text-sm font-semibold text-slate-700 py-3">{t('tickets:enrichment.ticketsAtJvid')}</AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-1.5 pb-3">
                        {tickets.map((tk) => (
                          <li key={tk.id}>
                            <Link to={`/tickets/${tk.id}`} onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50 transition-colors">
                              <span className="font-mono text-xs text-slate-500">{tk.ticketNumber}</span>
                              <TypeBadge type={tk.ticketType} />
                              <StatusBadge status={tk.status} />
                              <PriorityBadge priority={tk.priority} />
                              <span className="text-sm text-slate-700 truncate flex-1">{tk.subject}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Nearby addresses */}
                {nearby.length > 0 && (
                  <AccordionItem value="nearby" className="border border-slate-200 rounded-lg px-3">
                    <AccordionTrigger className="text-sm font-semibold text-slate-700 py-3">{t('tickets:enrichment.nearbyAddresses')}</AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-1 max-h-60 overflow-y-auto pb-3">
                        {nearby.map((a) => (
                          <li key={a.jvid} className="flex items-center gap-2 text-sm text-slate-600 px-2 py-1.5 rounded hover:bg-slate-50">
                            <Building2 className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                            <span className="truncate">
                              {[a.streetName, a.houseNumber, a.houseNumberExtension, a.busNumber].filter(Boolean).join(' ')}
                              {a.city ? `, ${a.postalCode} ${a.city}` : ''}
                            </span>
                            <span className="font-mono text-[11px] text-slate-400 ml-auto shrink-0">{a.jvid}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Orders table with inline workflow timeline ──────────────

function OrdersTable({
  orders, showSplitter, expandedOrder, workflows, onToggle,
}: {
  orders: DmpOrderResponse[];
  showSplitter: boolean;
  expandedOrder: number | null;
  workflows: Record<number, OrderWorkflowEntry[] | 'loading' | 'error'>;
  onToggle: (orderId: number) => void;
}) {
  const { t } = useTranslation(['tickets', 'common']);
  const colCount = showSplitter ? 6 : 5;
  return (
    <div className="border border-slate-200 rounded-lg overflow-x-auto mb-3">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-50 text-slate-400">
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('tickets:enrichment.cols.orderId')}</th>
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('common:fields.orderType')}</th>
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('common:fields.status')}</th>
            {showSplitter && <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('tickets:enrichment.cols.splitter')}</th>}
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('tickets:enrichment.cols.created')}</th>
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('tickets:enrichment.cols.executed')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {orders.map((o) => {
            const isOpen = expandedOrder === o.orderId;
            const wf = workflows[o.orderId];
            return (
              <Fragment key={o.orderId}>
                <tr
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={() => onToggle(o.orderId)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(o.orderId); } }}
                  className={`cursor-pointer hover:bg-slate-50 transition-colors ${isOpen ? 'bg-brand-rose/5' : ''}`}
                >
                  <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                    <ChevronRight className={`inline w-3 h-3 mr-1 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    {o.orderId}
                    {o.customerRef && <span className="text-slate-400 ml-1">· {o.customerRef}</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{o.orderType || DASH}</td>
                  <td className="px-3 py-2 text-slate-700">{o.orderStatus || DASH}</td>
                  {showSplitter && <td className="px-3 py-2 text-slate-700">{o.splitterRef ?? DASH}</td>}
                  <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtDate(o.createdAt)}</td>
                  <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtDate(o.technicallyExecutedAt)}</td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={colCount} className="px-3 py-2 bg-slate-50/60">
                      <WorkflowTimeline state={wf} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function WorkflowTimeline({ state }: { state: OrderWorkflowEntry[] | 'loading' | 'error' | undefined }) {
  const { t } = useTranslation('tickets');
  if (state === 'loading' || state === undefined) {
    return <div className="flex items-center gap-2 text-xs text-slate-400 py-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />{t('enrichment.loading')}</div>;
  }
  if (state === 'error') return <p className="text-xs text-red-500 py-2">{t('enrichment.workflowError')}</p>;
  if (state.length === 0) return <p className="text-xs text-slate-400 py-2">{t('enrichment.workflowNone')}</p>;
  return (
    <ol className="space-y-1.5 py-1">
      {state.map((e, i) => (
        <li key={i} className="flex gap-3 text-xs">
          <span className="text-slate-400 whitespace-nowrap w-24 shrink-0">{fmtDate(e.updatedAt)}</span>
          <span className="text-slate-700">
            <span className="font-medium">{e.fromStatus ? `${e.fromStatus} → ` : ''}{e.toStatus}</span>
            {e.timeslot && <span className="text-slate-400 ml-2">{e.timeslot}</span>}
            {e.delayReason && <span className="block text-amber-600">{e.delayReason}{e.delayReasonRemark ? ` — ${e.delayReasonRemark}` : ''}</span>}
            {/* DMP text shown verbatim; the server sends it to internal users and to operators whose switch is on. */}
            {e.delayReasonInternalRemark && (
              <span className="block text-slate-600 whitespace-pre-wrap break-words">
                <span className="font-medium">{t('enrichment.workflowRemark')}:</span> {e.delayReasonInternalRemark}
              </span>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

function SubscriptionsTable({ subs }: { subs: EnrichmentSubscription[] }) {
  const { t } = useTranslation('tickets');
  return (
    <div className="border border-slate-200 rounded-lg overflow-x-auto mb-3">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-50 text-slate-400">
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('enrichment.sub.operator')}</th>
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('enrichment.sub.type')}</th>
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('enrichment.sub.identifier')}</th>
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('enrichment.sub.sla')}</th>
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('enrichment.sub.start')}</th>
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">{t('enrichment.sub.end')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {subs.map((s, i) => (
            <tr key={i}>
              <td className="px-3 py-2 text-slate-700">{s.operator ?? DASH}</td>
              <td className="px-3 py-2 text-slate-700">{s.type ?? DASH}</td>
              <td className="px-3 py-2 text-slate-700 font-mono">{s.subscriptionIdentifier ?? DASH}</td>
              <td className="px-3 py-2 text-slate-700">{s.sla ?? DASH}</td>
              <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtDate(s.startDate)}</td>
              <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtDate(s.endDate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
